import { type Analytics, AnalyticsBrowser } from '@june-so/analytics-next';
import accounting from 'accounting';
import { bind } from 'bind-decorator';
import createContext from 'bz-utilities/hooks/context-v2';
import { client } from 'connect/core/client';
import { type AnalyticsEvents, analyticsEventMapping } from 'connect/core/enums/analytics-enum';
import { flattenPurchaseEventForGtag } from 'connect/core/utils/purchase-event';
import sanitize from 'connect/core/utils/sanitize-html';
import { action, computed, observable, runInAction } from 'mobx';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import type { OnlineSettingsResponse } from 'tm3-core/api.dtos';
import { License, OnlineSettingsRequest } from 'tm3-core/api.dtos';

export const [ProviderStoreContext, useProviderStore] =
  createContext<ProviderStore>('ProviderStore');

class ProviderStore {
  @observable allowClassBooking = false;
  @observable allowRegister = false;
  @observable allowPayAtClinic = false;
  @observable classes = false;
  @observable clinical = false;
  @observable currency = '£';
  @observable license = new License();
  @observable message = '';
  @observable payments = false;
  @observable siteTitle = 'Connect Online Bookings';
  @observable showSiteTitle = false;
  @observable logoUrl = '';
  @observable appIconUrl = '';
  @observable skin = 'default';
  @observable title = '';
  @observable dobRequired = false;
  @observable addressRequired = false;
  @observable cancellationPolicy: string = '';
  @observable termsAndConditions: string = '';
  @observable privacyPolicy: string = '';
  @observable customColourPrimary = '';
  @observable customColourSecondary = '';
  @observable customColourNav = '';
  @observable customColourNavText = '';
  @observable connectThemeSurface = '#FFFFFF';
  @observable connectThemeSurfaceMuted = '#F8FAFC';
  @observable connectThemeSurfaceSubtle = '#F1F5F9';
  @observable connectThemeSurfaceElevated = '#F8FAFC';
  @observable connectThemeTextDefault = '#0F172A';
  @observable connectThemeTextMuted = '#475569';
  @observable connectThemeTextSubtle = '#94A3B8';
  @observable connectThemeBorderDefault = '#E2E8F0';
  @observable connectThemeBorderMuted = '#F1F5F9';
  @observable connectThemeFocusRing = '#94A3B8';
  @observable connectThemeStatusSuccess = '#15803D';
  @observable connectThemeStatusSuccessSurface = '#DCFCE7';
  @observable connectThemeStatusWarning = '#92400E';
  @observable connectThemeStatusWarningSurface = '#FEF3C7';
  @observable connectThemeStatusCritical = '#B91C1C';
  @observable connectThemeStatusCriticalSurface = '#FEE2E2';
  @observable connectFontBodyFamily = 'Nunito Sans';
  @observable connectFontHeadingFamily = 'Nunito Sans';
  @observable stripeEnabled = false;
  @observable squareEnabled: boolean = false;
  @observable clearAcceptEnabled: boolean = false;
  @observable useExtraAddressLine = false;
  /** True only after analytics configuration has been accepted by the patient. */
  @observable analyticsEnabled: boolean = false;
  @observable analyticsConfigured: boolean = false;
  @observable embeddedMode = false;
  @observable isSiteLive: boolean = false;
  @observable saveCardMessage = '';
  @observable googleTrackingId: string = null;
  @observable connectEnabled: boolean = false;
  @observable videoIntegration: string = 'NONE';
  @observable hasZoomPro: boolean = false;
  @observable stripeApiKey: string | undefined = undefined;
  @observable stripeAccount: string | undefined = undefined;
  @observable squareApplicationId: string | null = null;
  @observable isSquareSandboxMode: boolean = false;
  @observable tagManagerId: string = '';
  @observable unauthenticatedMessage = '';
  @observable addressLabels = {
    ADDR1: 'Address Line 1',
    ADDR2: 'Address Line 2',
    ADDR3: 'Address 3',
    ADDR4: 'Address Line 4',
    ADDR5: 'Address Line 5',
    ADDRPC: 'Postcode'
  };
  @observable isVHG: boolean = false;
  @observable recaptchaSiteKey: string | undefined;
  @observable recaptchaEnabled = false;
  @observable allowWaitingListPayments = false;
  @observable applePayEnabled = false;
  @observable currencyCode = 'GBP';
  @observable location = 'GBR';
  @observable useSSO = false;
  @observable ssoDomain?: string;
  @observable _usingSimplifiedRegistration = false;

  /** The simplified form omits address and DOB fields, so it must be suppressed when either is required. */
  @computed get usingSimplifiedRegistration(): boolean {
    return this._usingSimplifiedRegistration && !this.addressRequired && !this.dobRequired;
  }
  @observable allowPatientEditDetails = true;
  @observable connectBookingFlowMode = 'Legacy';
  @observable magicLinkSentMessage = '';
  @observable insuranceDetailsMessage = '';

  @computed get useNextBookingFlow(): boolean {
    return this.connectBookingFlowMode === 'Next';
  }

  @computed get isClinical() {
    return true;
  }

  constructor(embeddedMode: boolean = false) {
    this.embeddedMode = embeddedMode;
  }

  @bind
  public onLoad(embeddedMode: boolean, businessId?: number) {
    this.embeddedMode = embeddedMode;

    return client
      .get(new OnlineSettingsRequest({ businessId }))
      .then((res: OnlineSettingsResponse) => {
        const {
          allowClassBooking,
          allowRegister,
          allowPayAtClinic,
          classes,
          clinical,
          currency,
          license,
          message,
          payments,
          siteTitle,
          showSiteTitle,
          customColourPrimary,
          customColourSecondary,
          customColourNav,
          customColourNavText,
          connectThemeSurface,
          connectThemeSurfaceMuted,
          connectThemeSurfaceSubtle,
          connectThemeSurfaceElevated,
          connectThemeTextDefault,
          connectThemeTextMuted,
          connectThemeTextSubtle,
          connectThemeBorderDefault,
          connectThemeBorderMuted,
          connectThemeFocusRing,
          connectThemeStatusSuccess,
          connectThemeStatusSuccessSurface,
          connectThemeStatusWarning,
          connectThemeStatusWarningSurface,
          connectThemeStatusCritical,
          connectThemeStatusCriticalSurface,
          connectFontBodyFamily,
          connectFontHeadingFamily,
          logoUrl,
          appIconUrl,
          skin,
          title,
          dobRequired,
          addressRequired,
          stripeEnabled,
          squareEnabled,
          clearAcceptEnabled,
          googleTrackingId,
          isSiteLive,
          cancellationPolicy,
          termsAndConditions,
          privacyPolicy,
          useExtraAddressLine,
          stripeApiKey,
          stripeAccount,
          squareApplicationId,
          isSquareSandboxMode,
          connectEnabled,
          labels,
          googleTagManagerContainerId,
          videoIntegration,
          hasZoomPro,
          saveCardMessage,
          isVHG,
          recaptchaSiteKey,
          allowWaitingListPayments,
          unauthenticatedMessage,
          useSSO,
          applePayEnabled,
          currencyCode,
          location,
          ssoDomain,
          usingSimplifiedRegistration,
          allowPatientEditDetails,
          connectBookingFlowMode,
          magicLinkSentMessage,
          insuranceDetailsMessage
        } = res;

        runInAction(() => {
          this.isVHG = isVHG;
          this.allowClassBooking = allowClassBooking;
          this.allowRegister = allowRegister;
          this.allowPayAtClinic = allowPayAtClinic;
          this.classes = classes;
          this.clinical = clinical;
          this.currency = currency;
          // Older/partial settings responses may omit the license object. Keep the
          // initialized empty license so consumers can safely read its flags.
          this.license = license || this.license;
          this.message = message ? sanitize(message) : '';
          this.payments = payments;
          this.siteTitle = siteTitle;
          this.showSiteTitle = showSiteTitle;
          this.logoUrl = logoUrl;
          this.appIconUrl = appIconUrl;
          this.customColourPrimary = customColourPrimary;
          this.customColourSecondary = customColourSecondary;
          this.customColourNav = customColourNav;
          this.customColourNavText = customColourNavText;
          this.connectThemeSurface = connectThemeSurface || this.connectThemeSurface;
          this.connectThemeSurfaceMuted = connectThemeSurfaceMuted || this.connectThemeSurfaceMuted;
          this.connectThemeSurfaceSubtle =
            connectThemeSurfaceSubtle || this.connectThemeSurfaceSubtle;
          this.connectThemeSurfaceElevated =
            connectThemeSurfaceElevated || this.connectThemeSurfaceElevated;
          this.connectThemeTextDefault = connectThemeTextDefault || this.connectThemeTextDefault;
          this.connectThemeTextMuted = connectThemeTextMuted || this.connectThemeTextMuted;
          this.connectThemeTextSubtle = connectThemeTextSubtle || this.connectThemeTextSubtle;
          this.connectThemeBorderDefault =
            connectThemeBorderDefault || this.connectThemeBorderDefault;
          this.connectThemeBorderMuted = connectThemeBorderMuted || this.connectThemeBorderMuted;
          this.connectThemeFocusRing = connectThemeFocusRing || this.connectThemeFocusRing;
          this.connectThemeStatusSuccess =
            connectThemeStatusSuccess || this.connectThemeStatusSuccess;
          this.connectThemeStatusSuccessSurface =
            connectThemeStatusSuccessSurface || this.connectThemeStatusSuccessSurface;
          this.connectThemeStatusWarning =
            connectThemeStatusWarning || this.connectThemeStatusWarning;
          this.connectThemeStatusWarningSurface =
            connectThemeStatusWarningSurface || this.connectThemeStatusWarningSurface;
          this.connectThemeStatusCritical =
            connectThemeStatusCritical || this.connectThemeStatusCritical;
          this.connectThemeStatusCriticalSurface =
            connectThemeStatusCriticalSurface || this.connectThemeStatusCriticalSurface;
          this.connectFontBodyFamily = connectFontBodyFamily || this.connectFontBodyFamily;
          this.connectFontHeadingFamily = connectFontHeadingFamily || this.connectFontHeadingFamily;
          this.skin = skin;
          this.dobRequired = dobRequired;
          this.addressRequired = addressRequired;
          this.title = title;
          this.stripeEnabled = stripeEnabled;
          this.analyticsConfigured = !!googleTrackingId || !!googleTagManagerContainerId;
          this.googleTrackingId = googleTrackingId;
          this.isSiteLive = isSiteLive;
          this.cancellationPolicy = cancellationPolicy ? sanitize(cancellationPolicy) : '';
          this.termsAndConditions = termsAndConditions ? sanitize(termsAndConditions) : '';
          this.privacyPolicy = privacyPolicy ? sanitize(privacyPolicy) : '';
          this.useExtraAddressLine = useExtraAddressLine;
          accounting.settings.currency.symbol = currency;
          this.stripeApiKey = stripeApiKey;
          this.stripeAccount = stripeAccount;
          this.connectEnabled = connectEnabled;
          this.videoIntegration = videoIntegration;
          this.hasZoomPro = hasZoomPro;
          this.tagManagerId = googleTagManagerContainerId;
          this.squareEnabled = squareEnabled;
          this.clearAcceptEnabled = clearAcceptEnabled;
          this.isSquareSandboxMode = isSquareSandboxMode;
          this.squareApplicationId = squareApplicationId;
          this.recaptchaSiteKey = recaptchaSiteKey;
          this.saveCardMessage = saveCardMessage;
          this.recaptchaEnabled = !!recaptchaSiteKey;
          this.allowWaitingListPayments = allowWaitingListPayments;
          labels.forEach((dl) => (this.addressLabels[dl.id] = dl.text));
          this.unauthenticatedMessage = unauthenticatedMessage;
          this.useSSO = useSSO;
          this.applePayEnabled = applePayEnabled;
          this.currencyCode = currencyCode;
          this.location = location;
          this.ssoDomain = ssoDomain;
          this._usingSimplifiedRegistration = usingSimplifiedRegistration;
          this.allowPatientEditDetails = allowPatientEditDetails;
          if (connectBookingFlowMode === 'Next' || connectBookingFlowMode === 'Legacy') {
            this.connectBookingFlowMode = connectBookingFlowMode;
          }
          this.magicLinkSentMessage = magicLinkSentMessage;
          this.insuranceDetailsMessage = insuranceDetailsMessage;
        });

        const resolvedConnectBookingFlowMode = this.connectBookingFlowMode;
        if (typeof document !== 'undefined') {
          document.title = this.siteTitle?.trim() || 'Connect Online Bookings';
        }

        const settingsPayload = {
          businessId,
          connectBookingFlowMode: resolvedConnectBookingFlowMode,
          sourceValue: connectBookingFlowMode
        };
        console.info('[Connect][ProviderStore] Settings loaded.', settingsPayload);
        if (typeof window !== 'undefined') {
          const history = ((window as any).__connectProviderSettingsLoads ?? []) as any[];
          history.push({ ...settingsPayload, at: new Date().toISOString() });
          (window as any).__connectProviderSettingsLoads = history;
        }

        const cookies = document.cookie.split(';');

        const essentialCookie = cookies.find((cookie) => {
          return cookie.trim() === 'connectGdprEssentialOnly=true';
        });

        const consentCookie = cookies.find((cookie) => {
          return cookie.trim() === 'connectGdprConsent=true';
        });
        if (!!consentCookie && !essentialCookie && this.analyticsConfigured) {
          this.enableAnalytics();
        } else {
          this.disableAnalytics();
        }
      });
  }

  async enableAnalytics() {
    if (!this.analyticsConfigured || this.analyticsEnabled) return;
    this.analyticsEnabled = true;

    if (!!this.googleTrackingId) {
      ReactGA.initialize(this.googleTrackingId);
      ReactGA.ga('require', 'ecommerce');
      const path = window.location.pathname || '/';
      ReactGA.send({ hitType: 'pageview', page: path });
    }

    if (!!this.tagManagerId) {
      window.dataLayer = window.dataLayer || [];
      const tagManagerArgs = {
        gtmId: this.tagManagerId
      };

      TagManager.initialize(tagManagerArgs);
    }
  }
  disableAnalytics() {
    this.analyticsEnabled = false;
    const cookies = document.cookie.split(';');
    cookies.forEach((cookie) => {
      if (cookie.trim().startsWith('_ga') || cookie.trim().startsWith('_gid')) {
        document.cookie = cookie + '; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    });
  }

  @action.bound
  sendAnalyticsEvent(analyticsEvent: AnalyticsEvents, params?: any) {
    if (!this.analyticsEnabled || !analyticsEvent) return;

    const eventNames = analyticsEventMapping[analyticsEvent];
    if (!eventNames?.ga) return;

    if (this.googleTrackingId) {
      // ReactGA owns the GA4 transport when a measurement ID is configured, for every
      // event including purchase - this takes priority over GTM when both a measurement
      // ID and a GTM container are configured, so a customer's GTM container GA4 tag
      // cannot double-fire the same event. See provider-store.vtest.ts for the tenant
      // configuration matrix this guards (GA4-only, GTM-only, both configured).
      const gaParams =
        eventNames.ga === 'purchase' && params && typeof params === 'object'
          ? flattenPurchaseEventForGtag(params as Record<string, unknown>)
          : params;
      ReactGA.event(eventNames.ga, gaParams);
      return;
    }

    // With GTM only, ReactGA has not been initialised and its lazy gtag
    // fallback would push an Arguments tuple rather than the `{ event: ... }`
    // message that GTM custom-event triggers consume.
    if (this.tagManagerId && typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ...(params && typeof params === 'object' ? params : {}),
        event: eventNames.ga
      });
    }
  }
}

export default ProviderStore;
